import { API_BASE_URL } from "../../../config";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from 'react-select';
import FactoryCounterNotice from './FactoryCounterNotice';

const CUSTOMER_API = `${API_BASE_URL}/customer/api`;
const ITEMS_API = `${API_BASE_URL}/seller/api/product-items`;
const SALE_FACTORY_API = `${API_BASE_URL}/saledata/api/module/sale-factory`;
const SALE_PROMO_API = `${API_BASE_URL}/saledata/api/module/sale-promo`;
const BAG_ITEMS_API = `${API_BASE_URL}/seller/api/sub-product-items/active`;
const BAG_SALE_FACTORY_API = `${API_BASE_URL}/saledata/api/module/bag-sales/factory`;

// Special agent ID representing the factory itself (no username) — the only
// agent that can additionally be paired with a specific regular customer,
// so the sale is still grouped under this agent's report but the real
// customer is tagged for later BI/purchase-history analysis.
const AGC_TOKYO_UID = 'AGC-TOKYO';
const isAgcTokyo = (uid) => String(uid || '').trim().toUpperCase() === AGC_TOKYO_UID;

const getToken = () => {
  const user = localStorage.getItem("user");
  if (!user) return null;
  try { return JSON.parse(user)?.token || null; } catch { return null; }
};

const useAuthHeaders = () => {
  const token = getToken();
  return useMemo(() => token ? ({
    accept: "*/*",
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  }) : ({ accept: "*/*", "Content-Type": "application/json" }), [token]);
};

const generateRef = (prefix = 'REG') => {
  const today = new Date();
  const y = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const dd = String(today.getDate()).padStart(2, '0');
  const base = `${prefix}-${y}${mm}${dd}`;
  const key = `REF_COUNTER_${base}`;
  let counter = Number(localStorage.getItem(key) || '0') + 1;
  if (counter > 9999) counter = 1;
  localStorage.setItem(key, String(counter));
  return `${base}-NEW-${String(counter).padStart(3, '0')}`;
};

const AgentSaleFactory = () => {
  const headers = useAuthHeaders();
  const [agents, setAgents] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedAgentUid, setSelectedAgentUid] = useState("");

  // AGC-TOKYO-only: pair the sale with a specific regular customer for BI
  const [regularCustomers, setRegularCustomers] = useState([]);
  const [selectedRegularCustomerUid, setSelectedRegularCustomerUid] = useState("");

  // Regular sale state
  const [regError, setRegError] = useState("");
  const [regSuccess, setRegSuccess] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemDetails, setItemDetails] = useState({});
  const [reference, setReference] = useState("");

  // Promo sale state
  const [promoError, setPromoError] = useState("");
  const [promoSuccess, setPromoSuccess] = useState("");
  const [promoItems, setPromoItems] = useState([]);
  const [promoItemDetails, setPromoItemDetails] = useState({}); // { [itemId]: { price, qty } }
  const [promoBagCount, setPromoBagCount] = useState("");
  const [promoRef, setPromoRef] = useState("");

  // Bag sale state (ซื้อขายกระสอบ)
  const [bagItems, setBagItems] = useState([]);
  const [bagError, setBagError] = useState("");
  const [bagSuccess, setBagSuccess] = useState("");
  const [selectedBagItems, setSelectedBagItems] = useState([]);
  const [bagItemDetails, setBagItemDetails] = useState({}); // { [itemId]: { price, qty } }
  const [bagRef, setBagRef] = useState("");

  const findAgentId = (a) => a?.agentCustomerUid || a?.uid || a?.id || a?.customerId;

  const logoutIfUnauthorized = (resp) => {
    if (resp.status === 401 || resp.status === 403) {
      localStorage.removeItem('user');
      window.location.href = '/login';
      return true;
    }
    return false;
  };

  const fetchAgents = useCallback(async () => {
    try {
      const resp = await fetch(`${CUSTOMER_API}/customers/by-category`, {
        method: 'POST', headers, body: JSON.stringify({ type: 'AGENT' }),
      });
      if (!resp.ok) throw new Error(`Load agents failed (${resp.status})`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.agent) ? data.agent : (Array.isArray(data?.data) ? data.data : []));
      setAgents(list);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchItems = useCallback(async () => {
    try {
      const resp = await fetch(ITEMS_API, { method: 'GET', headers });
      if (!resp.ok) throw new Error(`Load items failed (${resp.status})`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setItems(list);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchBagItems = useCallback(async () => {
    try {
      const resp = await fetch(BAG_ITEMS_API, { method: 'GET', headers });
      if (!resp.ok) throw new Error(`Load bag items failed (${resp.status})`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : []);
      setBagItems(list);
    } catch (e) { console.error(e); }
  }, [headers]);

  const fetchRegularCustomers = useCallback(async () => {
    try {
      const resp = await fetch(`${CUSTOMER_API}/customers/by-category`, {
        method: 'POST', headers, body: JSON.stringify({ type: 'REGULAR' }),
      });
      if (!resp.ok) throw new Error(`Load regular customers failed (${resp.status})`);
      const data = await resp.json();
      const list = Array.isArray(data) ? data : (Array.isArray(data?.regular) ? data.regular : (Array.isArray(data?.data) ? data.data : []));
      setRegularCustomers(list);
    } catch (e) { console.error(e); }
  }, [headers]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchAgents(), fetchItems(), fetchBagItems(), fetchRegularCustomers()]);
      setLoading(false);
      setReference(generateRef('REG'));
      setPromoRef(generateRef('PRO'));
      setBagRef(generateRef('BAG'));
    })();
  }, [fetchAgents, fetchItems, fetchBagItems, fetchRegularCustomers]);

  // Paired-customer selection only makes sense for AGC-TOKYO — clear it whenever
  // the chosen agent changes to anything else, so it can't bleed into another agent's sale.
  useEffect(() => {
    if (!isAgcTokyo(selectedAgentUid)) setSelectedRegularCustomerUid("");
  }, [selectedAgentUid]);

  // ── Regular helpers ──
  const toggleItem = (itemId) => {
    setSelectedItems(prev => {
      const exists = prev.includes(itemId);
      const next = exists ? prev.filter(id => id !== itemId) : [...prev, itemId];
      if (!exists) setItemDetails(d => ({ ...d, [itemId]: d[itemId] || { price: '', qty: '' } }));
      else setItemDetails(d => { const nd = { ...d }; delete nd[itemId]; return nd; });
      return next;
    });
  };

  const updateItemDetail = (itemId, field, value) => {
    setItemDetails(d => ({ ...d, [itemId]: { ...(d[itemId] || { price: '', qty: '' }), [field]: value } }));
  };

  const buildSaleCode = () =>
    selectedItems
      .filter(id => itemDetails[id]?.price !== '' && itemDetails[id]?.qty !== '')
      .map(id => `${id}I${itemDetails[id].price}B${itemDetails[id].qty}P`)
      .join('-');

  // ── Promo helpers ──
  const togglePromoItem = (itemId) => {
    setPromoItems(prev => {
      const exists = prev.includes(itemId);
      const next = exists ? prev.filter(id => id !== itemId) : [...prev, itemId];
      if (!exists) setPromoItemDetails(d => ({ ...d, [itemId]: d[itemId] || { price: '', qty: '' } }));
      else setPromoItemDetails(d => { const nd = { ...d }; delete nd[itemId]; return nd; });
      return next;
    });
  };

  const updatePromoItemDetail = (itemId, field, value) => {
    setPromoItemDetails(d => ({ ...d, [itemId]: { ...(d[itemId] || { price: '', qty: '' }), [field]: value } }));
  };

  const buildPromoSaleCode = () => {
    const bags = parseFloat(promoBagCount || '0');
    const parts = promoItems
      .filter(id => promoItemDetails[id]?.qty !== '' && promoItemDetails[id]?.price !== '')
      .map(id => {
        const item = items.find(it => String(it.id) === String(id));
        const code = item?.productCode || String(id);
        const price = promoItemDetails[id].price;
        const qty = promoItemDetails[id].qty;
        return `${code}${price}B${qty}P`;
      });
    const totalQty = promoItems.reduce((s, id) => s + (Number(promoItemDetails[id]?.qty) || 0), 0);
    const prefix = `PR_${totalQty}P${bags}B`;
    return parts.length ? `${prefix}-${parts.join('-')}` : prefix;
  };

  // ── Bag-sale helpers (ซื้อขายกระสอบ) ──
  const toggleBagItem = (itemId) => {
    setSelectedBagItems(prev => {
      const exists = prev.includes(itemId);
      const next = exists ? prev.filter(id => id !== itemId) : [...prev, itemId];
      if (!exists) setBagItemDetails(d => ({ ...d, [itemId]: d[itemId] || { price: '', qty: '' } }));
      else setBagItemDetails(d => { const nd = { ...d }; delete nd[itemId]; return nd; });
      return next;
    });
  };

  const updateBagItemDetail = (itemId, field, value) => {
    setBagItemDetails(d => ({ ...d, [itemId]: { ...(d[itemId] || { price: '', qty: '' }), [field]: value } }));
  };

  // BagSaleDataRequest.saleCode format: {bagCode}{price}B{qty}P (bagCode already ends in "IB",
  // e.g. "1IB10B3P") — MasterBagSaleData.saleCode is NOT NULL, so priceDetails-only requests
  // (which BagSaleCodeParser also accepts) fail at the DB layer; the encoded string is the only
  // path that's actually been exercised end-to-end (verified via curl against S04 2026-07-07).
  const buildBagSaleCode = () =>
    selectedBagItems
      .filter(id => bagItemDetails[id]?.price !== '' && Number(bagItemDetails[id]?.qty) > 0)
      .map(id => {
        const item = bagItems.find(it => String(it.id) === String(id));
        const bagCode = item?.productCode || String(id);
        return `${bagCode}${bagItemDetails[id].price}B${bagItemDetails[id].qty}P`;
      })
      .join('-');

  // ── Submit regular ──
  const submitSale = async () => {
    setRegError(""); setRegSuccess("");
    if (!selectedAgentUid) { setRegError('กรุณาเลือกตัวแทน'); return; }
    if (selectedItems.length === 0) { setRegError('กรุณาเลือกสินค้าอย่างน้อย 1 รายการ'); return; }
    const saleCode = buildSaleCode();
    if (!saleCode) { setRegError('กรุณากรอกราคาและจำนวนของสินค้าที่เลือก'); return; }

    const usedKey = 'USED_FACTORY_REFS';
    const used = JSON.parse(localStorage.getItem(usedKey) || '[]');
    let ref = reference || generateRef('REG');
    while (used.includes(ref)) ref = generateRef('REG');

    try {
      const token = getToken();
      if (!token) { localStorage.removeItem('user'); window.location.href = '/login'; return; }
      const pairedCustomerId = (isAgcTokyo(selectedAgentUid) && selectedRegularCustomerUid) ? selectedRegularCustomerUid : undefined;
      const resp = await fetch(SALE_FACTORY_API, {
        method: 'POST',
        headers: { ...headers, 'X-Request-ID': ref },
        body: JSON.stringify({ saleCode, customerId: selectedAgentUid, latitude: 15.2926009, longitude: 103.2982452, sellerId: 'TokyoFactory', reference: ref, ...(pairedCustomerId ? { pairedCustomerId } : {}) }),
      });
      if (logoutIfUnauthorized(resp)) return;
      if (!resp.ok) { const t = await resp.text(); throw new Error(t || `Sale create failed (${resp.status})`); }
      localStorage.setItem(usedKey, JSON.stringify(Array.from(new Set([...used, ref]))));
      setRegSuccess('สร้างรายการขายสำเร็จ');
      setSelectedItems([]); setItemDetails({}); setReference(generateRef('REG'));
    } catch (e) {
      setRegError(`ไม่สามารถสร้างรายการขายได้: ${e.message || e}`);
    }
  };

  // ── Submit promo ──
  const submitPromo = async () => {
    setPromoError(""); setPromoSuccess("");
    if (!selectedAgentUid) { setPromoError('กรุณาเลือกตัวแทน'); return; }
    if (promoItems.length === 0) { setPromoError('กรุณาเลือกสินค้าโปรโมชันอย่างน้อย 1 รายการ'); return; }
    if (!promoItems.some(id => promoItemDetails[id]?.price && Number(promoItemDetails[id]?.qty) > 0)) { setPromoError('กรุณากรอกราคาและจำนวนสินค้าโปรโมชัน'); return; }
    const bags = parseFloat(promoBagCount || '0');
    if (isNaN(bags) || bags < 0) { setPromoError('กรุณากรอกจำนวนถุงให้ถูกต้อง'); return; }

    const saleCode = buildPromoSaleCode();
    const usedKey = 'USED_PROMO_REFS';
    const used = JSON.parse(localStorage.getItem(usedKey) || '[]');
    let ref = promoRef || generateRef('PRO');
    while (used.includes(ref)) ref = generateRef('PRO');

    try {
      const token = getToken();
      if (!token) { localStorage.removeItem('user'); window.location.href = '/login'; return; }
      // sale-promo doesn't override sellerId from customerId (unlike sale-factory), so pairing
      // just means sending the paired regular customer as customerId directly, sellerId stays the agent.
      const promoCustomerId = (isAgcTokyo(selectedAgentUid) && selectedRegularCustomerUid) ? selectedRegularCustomerUid : selectedAgentUid;
      const resp = await fetch(SALE_PROMO_API, {
        method: 'POST',
        headers: { ...headers, 'X-Request-ID': ref },
        body: JSON.stringify({ saleCode, customerId: promoCustomerId, latitude: 15.2926009, longitude: 103.2982452, sellerId: selectedAgentUid, reference: ref, isAgent: true }),
      });
      if (logoutIfUnauthorized(resp)) return;
      if (!resp.ok) { const t = await resp.text(); throw new Error(t || `Promo create failed (${resp.status})`); }
      localStorage.setItem(usedKey, JSON.stringify(Array.from(new Set([...used, ref]))));
      setPromoSuccess('บันทึกรายการโปรโมชันสำเร็จ');
      setPromoItems([]); setPromoItemDetails({}); setPromoBagCount(""); setPromoRef(generateRef('PRO'));
    } catch (e) {
      setPromoError(`ไม่สามารถบันทึกรายการโปรโมชันได้: ${e.message || e}`);
    }
  };

  // ── Submit bag sale ──
  const submitBagSale = async () => {
    setBagError(""); setBagSuccess("");
    if (!selectedAgentUid) { setBagError('กรุณาเลือกตัวแทน'); return; }
    if (selectedBagItems.length === 0) { setBagError('กรุณาเลือกกระสอบอย่างน้อย 1 รายการ'); return; }
    const saleCode = buildBagSaleCode();
    if (!saleCode) { setBagError('กรุณากรอกราคาและจำนวนของกระสอบที่เลือก'); return; }

    const usedKey = 'USED_BAG_SALE_REFS';
    const used = JSON.parse(localStorage.getItem(usedKey) || '[]');
    let ref = bagRef || generateRef('BAG');
    while (used.includes(ref)) ref = generateRef('BAG');

    try {
      const token = getToken();
      if (!token) { localStorage.removeItem('user'); window.location.href = '/login'; return; }
      const resp = await fetch(BAG_SALE_FACTORY_API, {
        method: 'POST',
        headers: { ...headers, 'X-Request-ID': ref },
        body: JSON.stringify({ saleCode, customerId: selectedAgentUid, latitude: 15.2926009, longitude: 103.2982452, sellerId: 'TokyoFactory', reference: ref, promotion: false }),
      });
      if (logoutIfUnauthorized(resp)) return;
      if (!resp.ok) { const t = await resp.text(); throw new Error(t || `Bag sale create failed (${resp.status})`); }
      localStorage.setItem(usedKey, JSON.stringify(Array.from(new Set([...used, ref]))));
      setBagSuccess('บันทึกรายการซื้อขายกระสอบสำเร็จ');
      setSelectedBagItems([]); setBagItemDetails({}); setBagRef(generateRef('BAG'));
    } catch (e) {
      setBagError(`ไม่สามารถบันทึกรายการซื้อขายกระสอบได้: ${e.message || e}`);
    }
  };

  const totalQty = selectedItems.reduce((s, id) => s + (Number(itemDetails[id]?.qty) || 0), 0);
  const totalPrice = selectedItems.reduce((s, id) => {
    const d = itemDetails[id] || {};
    return s + (Number(d.price) || 0) * (Number(d.qty) || 0);
  }, 0);
  const promoTotalQty = promoItems.reduce((s, id) => s + (Number(promoItemDetails[id]?.qty) || 0), 0);
  const bagTotalQty = selectedBagItems.reduce((s, id) => s + (Number(bagItemDetails[id]?.qty) || 0), 0);
  const bagTotalPrice = selectedBagItems.reduce((s, id) => {
    const d = bagItemDetails[id] || {};
    return s + (Number(d.price) || 0) * (Number(d.qty) || 0);
  }, 0);

  const agentOptions = useMemo(
    () => agents.map(a => {
      const id = findAgentId(a);
      return { value: id, label: a.agentName || a.name || id };
    }),
    [agents]
  );
  const selectedAgentOption = useMemo(
    () => agentOptions.find(o => o.value === selectedAgentUid) || null,
    [agentOptions, selectedAgentUid]
  );

  const regularCustomerOptions = useMemo(
    () => regularCustomers.map(c => {
      const id = c?.customerUid || c?.uid || c?.id;
      return { value: id, label: c?.storeName || c?.customerName || id };
    }),
    [regularCustomers]
  );
  const selectedRegularCustomerOption = useMemo(
    () => regularCustomerOptions.find(o => o.value === selectedRegularCustomerUid) || null,
    [regularCustomerOptions, selectedRegularCustomerUid]
  );

  const sectionHeader = (label) => ({
    fontWeight: 700,
    fontSize: 15,
    color: '#343a40',
    margin: '0 0 12px 0',
    paddingBottom: 8,
    borderBottom: '2px solid #e9ecef',
  });

  return (
    <div className="btc-container">
      <h1>ขายส่งตัวแทน</h1>

      {loading ? <p>กำลังโหลดข้อมูล...</p> : (
        <div>

          {/* Agent selector — shared */}
          <div className="btc-card" style={{ padding: '16px 24px', marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#343a40' }}>ตัวแทน</label>
            <Select
              options={agentOptions}
              value={selectedAgentOption}
              onChange={(opt) => setSelectedAgentUid(opt ? opt.value : "")}
              placeholder="ค้นหา/เลือกตัวแทน..."
              isClearable
              className="btc-selectBox-container"
              classNamePrefix="btc-selectBox"
            />
          </div>

          {/* AGC-TOKYO only: the counter sells through IMD now — warn + show what IMD already booked (never blocks saving) */}
          {isAgcTokyo(selectedAgentUid) && (
            <FactoryCounterNotice
              headers={headers}
              refreshKey={`${regSuccess}|${promoSuccess}|${bagSuccess}`}
              regularCustomers={regularCustomers}
            />
          )}

          {/* AGC-TOKYO only: pair with a specific regular customer for BI */}
          {isAgcTokyo(selectedAgentUid) && (
            <div className="btc-card" style={{ padding: '16px 24px', marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, color: '#343a40' }}>ลูกค้าทั่วไป (ถ้ามี)</label>
              <Select
                options={regularCustomerOptions}
                value={selectedRegularCustomerOption}
                onChange={(opt) => setSelectedRegularCustomerUid(opt ? opt.value : "")}
                placeholder="ค้นหา/เลือกลูกค้าทั่วไป (ถ้ามี)..."
                isClearable
                className="btc-selectBox-container"
                classNamePrefix="btc-selectBox"
              />
            </div>
          )}

          {/* ── Regular sale ── */}
          <div className="btc-card" style={{ padding: '20px 24px', marginBottom: 16 }}>
            <p style={sectionHeader()}>ขายปกติ</p>

            {regError && <p className="error" style={{ marginBottom: 10 }}>{regError}</p>}
            {regSuccess && <p className="success" style={{ marginBottom: 10 }}>{regSuccess}</p>}

            <table className="btc-table" style={{ width: '100%', marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>สินค้า</th>
                  <th style={{ width: 110 }}>ราคา (฿)</th>
                  <th style={{ width: 90 }}>จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const checked = selectedItems.includes(it.id);
                  const det = itemDetails[it.id] || { price: '', qty: '' };
                  return (
                    <tr key={it.id} style={{ background: checked ? '#fff8f8' : undefined }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleItem(it.id)} />
                      </td>
                      <td style={{ fontWeight: checked ? 600 : undefined }}>{it.productName}</td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={det.price} disabled={!checked}
                          onChange={(e) => updateItemDetail(it.id, 'price', e.target.value)}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number" min="0"
                          value={det.qty} disabled={!checked}
                          onChange={(e) => updateItemDetail(it.id, 'qty', e.target.value)}
                          placeholder="0"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fa', borderRadius: 8, padding: '10px 14px' }}>
              <span style={{ fontSize: 14, color: '#495057' }}>
                {selectedItems.length === 0
                  ? 'ยังไม่ได้เลือกสินค้า'
                  : <span>เลือก <b>{selectedItems.length}</b> รายการ · <b>{totalQty}</b> ชิ้น · <b>{totalPrice.toLocaleString()}</b> ฿</span>
                }
              </span>
              <button
                className="btc-register-button"
                onClick={submitSale}
                disabled={!selectedAgentUid || selectedItems.length === 0}
              >
                บันทึกการขาย
              </button>
            </div>
          </div>

          {/* ── Promo sale ── */}
          <div className="btc-card" style={{ padding: '20px 24px' }}>
            <p style={sectionHeader()}>โปรโมชัน</p>

            {promoError && <p className="error" style={{ marginBottom: 10 }}>{promoError}</p>}
            {promoSuccess && <p className="success" style={{ marginBottom: 10 }}>{promoSuccess}</p>}

            <table className="btc-table" style={{ width: '100%', marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>สินค้า</th>
                  <th style={{ width: 110 }}>ราคา (฿)</th>
                  <th style={{ width: 90 }}>จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => {
                  const checked = promoItems.includes(it.id);
                  const det = promoItemDetails[it.id] || { price: '', qty: '' };
                  return (
                    <tr key={it.id} style={{ background: checked ? '#fff8f8' : undefined }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => togglePromoItem(it.id)} />
                      </td>
                      <td style={{ fontWeight: checked ? 600 : undefined }}>{it.productName}</td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={det.price} disabled={!checked}
                          onChange={(e) => updatePromoItemDetail(it.id, 'price', e.target.value)}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number" min="0"
                          value={det.qty} disabled={!checked}
                          onChange={(e) => updatePromoItemDetail(it.id, 'qty', e.target.value)}
                          placeholder="0"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <label style={{ fontWeight: 600, color: '#343a40', whiteSpace: 'nowrap', fontSize: 14 }}>ราคารวม (฿)</label>
              <input
                type="number" min="0" step="0.01"
                value={promoBagCount}
                onChange={(e) => setPromoBagCount(e.target.value)}
                placeholder="เช่น 500"
                style={{ width: 130, padding: '6px 10px', borderRadius: 6, border: '1px solid #ced4da', fontSize: 14 }}
              />
              <span style={{ fontSize: 12, color: '#6B6B6B' }}>
                saleCode: <code>{buildPromoSaleCode() || `PR_0P${promoBagCount || '?'}B`}</code>
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff8f8', borderRadius: 8, padding: '10px 14px', border: '1px solid #ffd0d0' }}>
              <span style={{ fontSize: 14, color: '#495057' }}>
                {promoItems.length === 0
                  ? 'ยังไม่ได้เลือกสินค้าโปรโมชัน'
                  : <span>เลือก <b>{promoItems.length}</b> ประเภท · รวม <b>{promoTotalQty}</b> ชิ้น</span>
                }
              </span>
              <button
                className="btc-register-button"
                onClick={submitPromo}
                disabled={!selectedAgentUid || promoItems.length === 0 || !promoBagCount || !promoItems.some(id => promoItemDetails[id]?.price && promoItemDetails[id]?.qty)}
                style={{ background: '#E60012' }}
              >
                บันทึกโปรโมชัน
              </button>
            </div>
          </div>

          {/* ── Bag sale (ซื้อขายกระสอบ) ── */}
          <div className="btc-card" style={{ padding: '20px 24px', marginTop: 16 }}>
            <p style={sectionHeader()}>ซื้อขายกระสอบ</p>

            {bagError && <p className="error" style={{ marginBottom: 10 }}>{bagError}</p>}
            {bagSuccess && <p className="success" style={{ marginBottom: 10 }}>{bagSuccess}</p>}

            <table className="btc-table" style={{ width: '100%', marginBottom: 12 }}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>กระสอบ</th>
                  <th style={{ width: 110 }}>ราคา (฿)</th>
                  <th style={{ width: 90 }}>จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {bagItems.map(it => {
                  const checked = selectedBagItems.includes(it.id);
                  const det = bagItemDetails[it.id] || { price: '', qty: '' };
                  return (
                    <tr key={it.id} style={{ background: checked ? '#fff8f8' : undefined }}>
                      <td style={{ textAlign: 'center' }}>
                        <input type="checkbox" checked={checked} onChange={() => toggleBagItem(it.id)} />
                      </td>
                      <td style={{ fontWeight: checked ? 600 : undefined }}>{it.productName}</td>
                      <td>
                        <input
                          type="number" min="0" step="0.01"
                          value={det.price} disabled={!checked}
                          onChange={(e) => updateBagItemDetail(it.id, 'price', e.target.value)}
                          placeholder="0.00"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                      <td>
                        <input
                          type="number" min="0"
                          value={det.qty} disabled={!checked}
                          onChange={(e) => updateBagItemDetail(it.id, 'qty', e.target.value)}
                          placeholder="0"
                          style={{ width: '100%', padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da', opacity: checked ? 1 : 0.3 }}
                        />
                      </td>
                    </tr>
                  );
                })}
                {bagItems.length === 0 && (
                  <tr><td colSpan="4" style={{ textAlign: 'center', color: '#adb5bd', padding: '10px' }}>ไม่มีรายการกระสอบ</td></tr>
                )}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fa', borderRadius: 8, padding: '10px 14px' }}>
              <span style={{ fontSize: 14, color: '#495057' }}>
                {selectedBagItems.length === 0
                  ? 'ยังไม่ได้เลือกกระสอบ'
                  : <span>เลือก <b>{selectedBagItems.length}</b> รายการ · <b>{bagTotalQty}</b> ใบ · <b>{bagTotalPrice.toLocaleString()}</b> ฿</span>
                }
              </span>
              <button
                className="btc-register-button"
                onClick={submitBagSale}
                disabled={!selectedAgentUid || selectedBagItems.length === 0}
              >
                บันทึกซื้อขายกระสอบ
              </button>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default AgentSaleFactory;
